import { useState, useEffect, useCallback } from 'react';
import {
  fetchOverviewStats,
  fetchFormActivity,
  fetchFormsWithCounts,
  fetchWeeklyPerformance,
} from '~/services/analytics';
import type {
  IDateRange,
  IDashboardOverviewStats,
  IFormActivityRow,
  IFormWithCount,
  IPerformanceDataPoint,
} from '~/types';

export function useDashboardData(dateRange: IDateRange) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<IDashboardOverviewStats | null>(null);
  const [forms, setForms] = useState<IFormWithCount[]>([]);
  const [selectedFormId, setSelectedFormId] = useState<string | undefined>(undefined);
  const [activityPage, setActivityPage] = useState(1);
  const [activityRows, setActivityRows] = useState<IFormActivityRow[]>([]);
  const [activityTotal, setActivityTotal] = useState(0);
  const [performance, setPerformance] = useState<IPerformanceDataPoint[]>([]);

  const loadCore = useCallback(async () => {
    setLoading(true);
    try {
      const [statsData, formsData, perfData] = await Promise.all([
        fetchOverviewStats(dateRange),
        fetchFormsWithCounts(),
        fetchWeeklyPerformance(dateRange),
      ]);
      setStats(statsData);
      setForms(formsData);
      setPerformance(perfData);
    } catch (err) {
      console.error('Dashboard data error:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  const loadActivity = useCallback(async () => {
    try {
      const { rows, total } = await fetchFormActivity(dateRange, selectedFormId, activityPage);
      setActivityRows(rows);
      setActivityTotal(total);
    } catch (err) {
      console.error('Activity data error:', err);
    }
  }, [dateRange, selectedFormId, activityPage]);

  useEffect(() => {
    loadCore();
  }, [loadCore]);

  useEffect(() => {
    loadActivity();
  }, [loadActivity]);

  // Reset page when form filter changes
  useEffect(() => {
    setActivityPage(1);
  }, [selectedFormId]);

  return {
    loading,
    stats,
    forms,
    selectedFormId,
    setSelectedFormId,
    activityPage,
    setActivityPage,
    activityRows,
    activityTotal,
    performance,
  };
}
